import { useRef } from 'react'
import { useUiState } from '@/core/state/react'
import { useLeafletMap } from './useLeafletMap'
import './map.css'

/**
 * The map (Phase 1J) — the last piece of the shell to become React.
 *
 * It renders the container and creates the Leaflet map; everything that draws
 * INTO the map is still Vue, and untouched. That works because the four things
 * needing the map object stopped taking it as a prop or an argument in the
 * commits before this one and read `core/commands/mapInstance` instead. The
 * producer changed; no consumer did.
 *
 * Laid out against the edge the stat bar publishes, exactly as MapView did:
 * the map starts where the chrome ends. `position: fixed`, so the number is a
 * viewport coordinate and needs no offsetParent to measure against.
 */

/** Where the map starts before the bar has measured itself. MapView's number. */
const DEFAULT_MAP_TOP = 128

export function MapPanel() {
  const container = useRef<HTMLDivElement>(null)
  useLeafletMap(container)

  const chromeBottom = useUiState((s) => s.chromeBottom)
  const barVisible = useUiState((s) => s.quickFilterBar)
  const collapsed = useUiState((s) => s.quickFilterCollapsed)

  // The map's own right-hand controls share that side with the fixed Quick
  // Filters panel — 200px wide expanded, 40px collapsed, 1px from the edge —
  // and step left of it. 12px is their own inset. MapView set this on its root
  // and Leaflet's corners read it; it is set here now, for the same reason.
  const rightInset = !barVisible ? '12px' : collapsed ? '52px' : '212px'

  return (
    <div
      className="scvc-map-panel"
      style={{
        top: chromeBottom == null ? DEFAULT_MAP_TOP : Math.round(chromeBottom),
        ['--map-right-inset' as string]: rightInset,
      }}
    >
      {/* `data-tour="map"` is the guided tour's first step, and `.map-container`
          is what the report generator screenshots — both are contracts. */}
      <div ref={container} data-tour="map" className="map-container" />
    </div>
  )
}
